import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MenuBarComponent } from "../../core/components/menu-bar/menu-bar.component";
import { ImageCarouselComponent } from "../../core/components/image-carousel/image-carousel.component";
import { FooterComponent } from "../../core/components/footer/footer.component";
import { Project } from '../../core/interfaces/Project';
import { combineLatest } from 'rxjs';
import { BackendService } from '../../core/services/backend.service';
import { NgxTgModule } from '@worthy-ventures/metaglotta-ngx';
import { RichTextPipe } from '../../core/pipes/rich-text.pipe';

@Component({
  selector: 'app-projects',
  imports: [MenuBarComponent, ImageCarouselComponent, FooterComponent, NgxTgModule, RichTextPipe],
  templateUrl: './projects.component.html',
  styleUrl: './projects.component.scss'
})
export class ProjectsComponent implements AfterViewInit, OnDestroy {

  carouselItems: Project[] = [];
  project: Project | undefined;

  /**
   * Gates the fade-in on the hero photo.
   *
   * Safe as a plain field because the route is configured not to be reused, so
   * a new project means a new component instance and this starts false again.
   * It stays false when a project has no photo — the backend nulls imageUrl if
   * signing fails — and when the photo fails to load, both of which leave the
   * frame showing its placeholder rather than a broken image.
   */
  imageLoaded = false;

  /**
   * The copy column ends level with the image and scrolls, so it needs to say
   * so. Three states rather than one: whether there is anything to scroll at
   * all (short copy gets no affordance), whether the reader has started, and
   * whether they have reached the end.
   */
  copyScrollable = false;
  copyScrolled = false;
  copyAtEnd = false;

  /** Shown until the reader starts, and again if they scroll back to the top. */
  get showScrollHint(): boolean {
    return this.copyScrollable && !this.copyScrolled;
  }

  /** The fade is the "more below" cue, so it goes once there is no more below. */
  get showCopyFade(): boolean {
    return this.copyScrollable && !this.copyAtEnd;
  }

  @ViewChild('copyScroll') private copyScroll?: ElementRef<HTMLElement>;

  /** Content arrives async and images settle late, so re-measure on any resize. */
  private resizeObserver?: ResizeObserver;

  constructor(private route: ActivatedRoute, private router: Router, private backendService: BackendService) {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
  }

  ngOnInit() {
    combineLatest([
      this.backendService.getProjects(),
      this.route.paramMap
    ]).subscribe(([items, params]) => {
      const fetchedProjects : Project[] = items.data;
      const slug = params.get('slug');
      // Matched on the slug, not the title: titles come back localised, so a
      // title-keyed URL resolved only in the language it was copied from and
      // switching language on this page left every field blank.
      this.project = fetchedProjects.find(item => item.slug === slug);
      window.scrollTo({ top: 0, behavior: 'instant' });
      //set carouselItems except the current project
      this.carouselItems = fetchedProjects.filter(item => item.slug !== slug);
      // the copy is rendered from this, so measure once it is on the page
      queueMicrotask(() => this.measureCopy());
    });
  }

  ngAfterViewInit(): void {
    const el = this.copyScroll?.nativeElement;
    if (!el || typeof ResizeObserver === 'undefined') return;
    // catches the copy arriving, a font swapping in, and the column resizing
    this.resizeObserver = new ResizeObserver(() => this.measureCopy());
    this.resizeObserver.observe(el);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  onCopyScroll(): void {
    this.measureCopy();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.measureCopy();
  }

  /**
   * A few pixels of slack on every comparison: scrollTop is fractional on
   * zoomed or hidpi displays, so `scrollTop >= max` can miss the last pixel and
   * leave the fade up at the bottom of the scroll.
   */
  private measureCopy(): void {
    const el = this.copyScroll?.nativeElement;
    if (!el) return;
    const max = el.scrollHeight - el.clientHeight;
    this.copyScrollable = max > 4;
    this.copyScrolled = el.scrollTop > 4;
    this.copyAtEnd = max <= 4 || el.scrollTop >= max - 4;
  }
}
